import { ModalBottomButtons } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { ProfileSelection, SelectableMember } from 'components/ProfileSelection/ProfileSelection';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import type { BpClassType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { useTranslation } from 'react-i18next';
import { type CombinedError, useMutation, useQuery } from 'urql';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import styles from './ClassProfilesForm.module.scss';

const UpdateClassMutationDocument = graphql(`
  mutation UpdateClass($update: ClassUpdateInput!, $where: ClassWhere!) {
    updateClasses(update: $update, where: $where) {
      classes {
        uuid
        __typename
        schoolYear {
          uuid
          __typename
        }
        members {
          uuid
          __typename
        }
      }
    }
  }
`);

const ClassProfilesFormPageQueryDocument = graphql(`
  query ClassProfilesFormPageData($organizationUuid: ID!) {
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: { selectName: ASC } }) {
      __typename
      uuid
      selectName
      organizationRoles
    }
    ...ProfileSelectionFields
  }
`);

type ClassProfilesFormProps = {
  bpClass: BpClassType;
  schoolYearUuid: string;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

type ClassesProfileFormType = {
  profiles: SelectableMember[];
};

export const ClassProfilesForm = ({ bpClass, schoolYearUuid, onLoad, onClose }: ClassProfilesFormProps) => {
  const { t } = useTranslation();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();

  const [, updateClass] = useMutation(UpdateClassMutationDocument);

  const [{ data }] = useQuery({
    query: ClassProfilesFormPageQueryDocument,
    variables: {
      organizationUuid,
      schoolYearUuid: schoolYearUuid,
    },
  });

  const profiles: SelectableMember[] =
    data?.profiles
      .filter((p) => bpClass.profileUuids.includes(p.uuid))
      .map((p) => ({
        uuid: p?.uuid ?? '',
        name: p?.selectName ?? '',
        organizationRoles: p?.organizationRoles ?? [],
        selected: false,
      })) ?? [];

  const initialValues: ClassesProfileFormType = {
    profiles: profiles,
  };

  const handleSubmit = async (values: ClassesProfileFormType, formHelpers: FormikHelpers<ClassesProfileFormType>) => {
    onLoad(true);

    let result: { error?: CombinedError | undefined } = {};

    const initialProfilesUuids = bpClass.profileUuids;
    const newProfilesUuids = values.profiles.map((p) => p.uuid);

    const profilesToConnect = newProfilesUuids.filter(
      (newProfile) => !initialProfilesUuids.some((initialProfile) => initialProfile === newProfile),
    );

    const profilesToDisconnect = initialProfilesUuids.filter(
      (initialProfile) => !newProfilesUuids.some((newProfile) => initialProfile === newProfile),
    );

    result = await updateClass({
      update: {
        schoolYear: {
          connect: { where: { node: { uuid: schoolYearUuid } } },
        },
        members: [
          {
            connect: profilesToConnect.map((uuid) => {
              return {
                where: {
                  node: {
                    uuid: uuid,
                  },
                },
              };
            }),
            disconnect: profilesToDisconnect.map((uuid) => {
              return {
                where: {
                  node: {
                    uuid: uuid,
                  },
                },
              };
            }),
          },
        ],
      },
      where: {
        uuid: bpClass.uuid,
      },
    });

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessToast(t('classes.profilesEdited', { name: bpClass.name }));
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  };

  return (
    <Formik<ClassesProfileFormType>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnBlur={true}
      enableReinitialize={true}
    >
      {({ errors, values, setFieldTouched, setFieldValue, isSubmitting, dirty, isValidating }) => {
        return (
          <Form className={styles['class-profiles-form']}>
            <ProfileSelection
              categoriesOnly
              initialMembers={values.profiles}
              onPick={async (profiles) => {
                setFieldTouched('profiles');
                await setFieldValue('profiles', profiles);
              }}
              schoolYearUuid={bpClass.schoolYearUuid}
              query={data}
            />

            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              submitButton={{
                disabled: isSubmitting || !dirty || isValidating,
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
